import { Component, Inject, ChangeDetectionStrategy } from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogModule,
} from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";

export interface PrivacyWarningDialogData {
  competitionName: string;
  /** `null` when the caller cannot tell — the public invite preview served to
   * the /join page carries no privacy flag for a token-shared competition. The
   * template only shows the public-competition box on an explicit `true`. */
  isPublic: boolean | null;
}

@Component({
  selector: "app-privacy-warning-dialog",
  imports: [MatDialogModule, MatButtonModule, MatIconModule],
  template: `
    <div class="privacy-warning-dialog">
      <h2 mat-dialog-title>
        <mat-icon class="warning-icon">visibility</mat-icon>
        Before You Join
      </h2>

      <mat-dialog-content>
        <div class="warning-content">
          <p class="main-warning">
            <strong
              >Your habit names will be visible to other participants</strong
            >
            in this competition.
          </p>

          @if (data.isPublic === true) {
          <div class="info-box">
            <mat-icon>public</mat-icon>
            <span
              >This is a <strong>public competition</strong> — anyone can join
              and see participant data.</span
            >
          </div>
          }

          <p class="detail-text">Other participants will be able to see:</p>
          <ul>
            <li>Your username</li>
            <li>
              Your habit names (e.g., "Morning meditation", "Take vitamins")
            </li>
            <li>Your completion stats and streaks</li>
            <li>Your points and ranking</li>
          </ul>
        </div>
      </mat-dialog-content>

      <mat-dialog-actions class="dialog-actions">
        <button
          mat-raised-button
          color="primary"
          class="confirm-button"
          (click)="onConfirm()"
        >
          I Understand, Join Competition
        </button>
        <button mat-button class="cancel-button" (click)="onCancel()">
          Cancel
        </button>
      </mat-dialog-actions>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .privacy-warning-dialog {
        max-width: 480px;
      }

      h2[mat-dialog-title] {
        display: flex;
        align-items: center;
        gap: 8px;
        color: #e65100;
        margin-bottom: 16px;
      }

      .warning-icon {
        color: #e65100;
      }

      .warning-content {
        color: #333;
      }

      .main-warning {
        font-size: 1.1em;
        margin-bottom: 16px;
        padding: 12px;
        background: #fff3e0;
        border-left: 4px solid #e65100;
        border-radius: 4px;
      }

      .info-box {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px 12px;
        background: #e3f2fd;
        border-radius: 4px;
        margin-bottom: 16px;
        font-size: 0.9em;
      }

      .info-box mat-icon {
        color: #1976d2;
        font-size: 20px;
        width: 20px;
        height: 20px;
      }

      .detail-text {
        margin-bottom: 8px;
        color: #555;
      }

      ul {
        margin: 0 0 16px 0;
        padding-left: 24px;
        color: #555;
      }

      ul li {
        margin-bottom: 4px;
      }

      mat-dialog-actions.dialog-actions {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content: center;
        align-items: center;
        gap: 8px;
        padding: 16px 8px 0 8px;
        margin-top: 8px;
        min-height: 0;
      }

      /* Both buttons stay on one row; the long confirm label wraps inside the
         button instead of pushing Cancel out of the dialog on narrow screens. */
      .dialog-actions .confirm-button {
        flex: 0 1 auto;
        min-width: 0;
        height: auto;
        min-height: 40px;
        padding: 8px 16px;
        margin: 0;
        white-space: normal;
        line-height: 1.2;
        text-align: center;
      }

      .dialog-actions .cancel-button {
        flex: 0 0 auto;
        margin: 0;
      }
    `,
  ],
})
export class PrivacyWarningDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<PrivacyWarningDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PrivacyWarningDialogData
  ) {}

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onConfirm(): void {
    this.dialogRef.close(true);
  }
}
